import './styles.scss';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import Modal from 'antd/lib/modal';
import Result from 'antd/lib/result';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import copy from 'copy-to-clipboard';
import { ArrowLeftOutlined, CopyOutlined } from '@ant-design/icons';

import { getCore, Project } from 'cvat-core-wrapper';
import { CombinedState } from 'reducers';
import { deleteProjectAsync } from 'actions/projects-actions';
import FolderManager from 'components/folder-manager/folder-manager';
import LabelsEditor from 'components/labels-editor/labels-editor';
import StepDefaultsModal from 'components/annotation-page/workspace/steps/step-defaults-modal';
import type { RemoteFile } from 'components/file-manager/remote-browser';
import { plural, t } from 'utils/i18n';
import { fetchProjectFolder, ProjectFolder } from './project-page';
import { folderRequest } from './project-settings';

const core = getCore();
const NAME_LABEL = 'cvat-project-settings-name-label';

interface RowProps {
    title: string;
    hint: string;
    labelId?: string;
    children: React.ReactNode;
}

// one setting: what it is on the left, the control on the right, so no hairlines are needed
function Row({
    title, hint, labelId, children,
}: RowProps): JSX.Element {
    return (
        <div className='cvat-project-settings-row' aria-label={title}>
            <div className='cvat-project-settings-text'>
                <span className='cvat-project-settings-title' id={labelId}>{title}</span>
                <Text type='secondary'>{hint}</Text>
            </div>
            <div className='cvat-project-settings-control'>{children}</div>
        </div>
    );
}

export default function ProjectSettingsPage(): JSX.Element {
    const id = +useParams<{ id: string }>().id;
    const history = useHistory();
    const dispatch = useDispatch();
    const deletes = useSelector((state: CombinedState) => state.projects.activities.deletes);
    const [project, setProject] = useState<Project | null>(null);
    const [folder, setFolder] = useState<ProjectFolder | null>(null);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [picking, setPicking] = useState(false);
    const [picked, setPicked] = useState<string | null>(null);
    const [changing, setChanging] = useState(false);
    // inline, under the row it belongs to: a toast in the corner is easy to miss
    const [error, setError] = useState<{ row: string; text: string } | null>(null);
    const [defaultsOpen, setDefaultsOpen] = useState(false);
    const [editingLabels, setEditingLabels] = useState(false);

    useEffect(() => {
        let cancelled = false;
        core.projects.get({ id })
            .then(([loaded]: Project[]) => {
                if (cancelled || !loaded) return;
                setProject(loaded);
                setName(loaded.name);
            })
            .catch(() => undefined)
            .finally(() => { if (!cancelled) setLoading(false); });
        fetchProjectFolder(id).then((value) => { if (!cancelled) setFolder(value); }).catch(() => undefined);
        return () => { cancelled = true; };
    }, [id]);

    useEffect(() => {
        if (project && deletes[id]) history.push('/projects');
    }, [deletes]);

    if (loading) return <Spin size='large' className='cvat-spinner' />;
    if (!project) {
        return <Result className='cvat-not-found' status='404' title={t('The project was not found')} />;
    }

    const save = async (row: string, done: string, apply: (target: Project) => void): Promise<void> => {
        setSaving(true);
        setError(null);
        try {
            apply(project);
            setProject(await project.save());
            message.success(done);
        } catch (e: unknown) {
            setError({ row, text: e instanceof Error ? e.message : String(e) });
        } finally {
            setSaving(false);
        }
    };
    const rowError = (row: string): JSX.Element | null => (
        error?.row === row ? <Alert type='error' showIcon message={error.text} className='cvat-project-settings-error' /> : null
    );
    const rename = (): void => {
        if (!saving && name.trim() && name.trim() !== project.name) {
            save('name', t('Renamed'), (target) => { target.name = name.trim(); });
        }
    };

    const shownPath = folder ? folder.windows_path ?? folder.path : null;
    const copyPath = (): void => {
        if (shownPath && copy(shownPath)) message.success(t('Path copied'));
        else message.error(t('Could not copy the path'));
    };
    const changeFolder = async (): Promise<void> => {
        if (!picked) return;
        const request = folderRequest(folder?.id ?? null, picked);
        setChanging(true);
        setError(null);
        try {
            await core.server.request(`${core.config.backendAPI}/projects/${id}/project_source`, request);
            setFolder(await fetchProjectFolder(id));
            setPicking(false);
            setPicked(null);
            message.success(t('Folder changed'));
        } catch (e: unknown) {
            setPicking(false);
            setError({ row: 'folder', text: e instanceof Error ? e.message : String(e) });
        } finally {
            setChanging(false);
        }
    };
    const confirmDelete = (): void => {
        Modal.confirm({
            title: t('Delete the project “{name}”?', { name: project.name }),
            content: t('All its tasks, references and annotations are lost.'),
            okText: t('Delete'),
            okButtonProps: { danger: true },
            onOk: () => dispatch(deleteProjectAsync(project)),
        });
    };

    return (
        <div className='cvat-project-page cvat-project-settings-page'>
            <div className='cvat-project-page-content'>
                <div className='cvat-project-heading-text'>
                    <Button
                        type='link'
                        size='small'
                        className='cvat-project-settings-back'
                        icon={<ArrowLeftOutlined />}
                        onClick={() => history.push(`/projects/${id}`)}
                    >
                        {project.name}
                    </Button>
                    <h1>{t('Settings')}</h1>
                </div>
                <section className='cvat-project-settings-group' aria-label={t('General')}>
                    <h2>{t('General')}</h2>
                    <Row title={t('Name')} hint={t('Shown on the Projects page and in the workspace')} labelId={NAME_LABEL}>
                        <Input
                            aria-labelledby={NAME_LABEL}
                            value={name}
                            disabled={saving}
                            onChange={(event) => setName(event.target.value)}
                            onPressEnter={rename}
                        />
                        <Button
                            loading={saving}
                            disabled={!name.trim() || name.trim() === project.name}
                            onClick={rename}
                        >
                            {t('Rename')}
                        </Button>
                        {rowError('name')}
                    </Row>
                    <Row title={t('Folder')} hint={t('Where the team keeps the drawings and photos')}>
                        {shownPath ? (
                            <Text className='cvat-project-folder-path' title={shownPath}>{shownPath}</Text>
                        ) : (
                            <Text type='secondary'>{t('No folder yet')}</Text>
                        )}
                        {shownPath && <Button icon={<CopyOutlined />} onClick={copyPath}>{t('Copy path')}</Button>}
                        <Button onClick={() => setPicking(true)}>{shownPath ? t('Change folder') : t('Choose folder')}</Button>
                        {rowError('folder')}
                    </Row>
                    <Row title={t('Members')} hint={t('Everyone in the organization can open the project')}>
                        <Text>{project.owner ? `${project.owner.username} · ${t('owner')}` : t('No owner')}</Text>
                    </Row>
                </section>
                <section className='cvat-project-settings-group' aria-label={t('Annotation')}>
                    <h2>{t('Annotation')}</h2>
                    <Row title={t('Processing')} hint={t('Which models run each step on new tasks')}>
                        <Button onClick={() => setDefaultsOpen(true)}>{t('Models and defaults')}</Button>
                    </Row>
                    <Row title={t('Labels')} hint={plural(project.labels.length, 'label')}>
                        <Button onClick={() => setEditingLabels(!editingLabels)}>
                            {editingLabels ? t('Hide editor') : t('Edit labels')}
                        </Button>
                        {rowError('labels')}
                    </Row>
                    {editingLabels && (
                        <div className='cvat-project-settings-labels'>
                            <LabelsEditor
                                labels={project.labels.map((label) => label.toJSON())}
                                onSubmit={(labels) => save('labels', t('Labels saved'), (target) => {
                                    target.labels = labels.map((data) => new core.classes.Label(data));
                                })}
                            />
                        </div>
                    )}
                </section>
                <section className='cvat-project-settings-group' aria-label={t('Delete')}>
                    <Row title={t('Delete project')} hint={t('Removes its tasks, references and annotations')}>
                        <Button danger onClick={confirmDelete}>{t('Delete')}</Button>
                    </Row>
                </section>
            </div>
            {picking && (
                <Modal
                    visible
                    title={t('Change folder')}
                    okText={t('Change')}
                    okButtonProps={{ disabled: !picked, loading: changing }}
                    onOk={changeFolder}
                    onCancel={() => { setPicking(false); setPicked(null); }}
                >
                    <FolderManager
                        many={false}
                        onChangeActiveKey={() => undefined}
                        onUploadShareFolder={(folders: RemoteFile[]) => setPicked(folders[0]?.key ?? null)}
                    />
                </Modal>
            )}
            {defaultsOpen && <StepDefaultsModal projectId={id} onClose={() => setDefaultsOpen(false)} />}
        </div>
    );
}
